import type { QuoteLine } from './quote'

export type CaseStatus =
  | 'Ny henvendelse'
  | 'Kontaktet'
  | 'Tilbud sendt'
  | 'Accepteret'
  | 'Planlagt'
  | 'I gang'
  | 'Færdig'
  | 'Faktureret'

export type CaseCategory = 'Multiservice' | 'Montage' | 'Vedligehold' | 'Andet'

export type PriceModel = 'Timepris' | 'Fast pris' | 'Tilbud endnu ikke lavet'

export type PhotoStage = 'Før' | 'Under arbejdet' | 'Efter'

/** "Uploader" er en midlertidig, ufærdig henvendelse — den vises aldrig i admin. */
export type EnquiryStatus =
  | 'Ny henvendelse'
  | 'Accepteret'
  | 'Behandlet'
  | 'Afvist'
  | 'Uploader'

export const enquiryStatuses: Exclude<EnquiryStatus, 'Uploader'>[] = [
  'Ny henvendelse',
  'Accepteret',
  'Behandlet',
  'Afvist',
]

export interface CaseNote {
  id: string
  text: string
  createdAt: string
}

export interface CasePhoto {
  id: string
  /** Downloadadresse fra Firebase Storage. */
  url: string
  /** Sti i Firebase Storage, fx cases/{id}/billede.jpg */
  path: string
  name: string
  stage: PhotoStage
}

export interface Case {
  id: string
  number: string
  customerId: string
  customerName: string
  title: string
  address: string
  description: string
  category: CaseCategory
  priceModel: PriceModel
  hourlyRate: string
  fixedPrice: string
  estimate: string
  finalAmount: string
  plannedDate: string
  status: CaseStatus
  internalNotes: CaseNote[]
  photos: CasePhoto[]
  createdAt: string
  updatedAt: string
  fromWebsite: boolean
  enquiryId?: string
  /** Sat når sagen er oprettet fra et tilbud. */
  quoteId?: string
  quoteNumber?: string
  /** Kopi af tilbuddets linjer — forberedt til senere fakturering i Dinero. */
  quoteLines?: QuoteLine[]
  /** Sat når der er oprettet en faktura fra sagen. */
  invoiceId?: string
  invoiceNumber?: string
  /** Storage-stier pr. trin — holdes i sync med photos. */
  beforeImages?: string[]
  duringImages?: string[]
  afterImages?: string[]
}

export interface Customer {
  id: string
  name: string
  phone: string
  email: string
  address: string
  notes: string
  createdAt: string
}

export interface EnquiryImage {
  path: string
  url: string
  name: string
}

export interface Enquiry {
  id: string
  name: string
  phone: string
  email: string
  address: string
  description: string
  status: EnquiryStatus
  images: EnquiryImage[]
  createdAt: string
  caseId?: string
}

export interface AdminSettings {
  companyName: string
  tagline: string
  cvr: string
  phone: string
  email: string
  address: string
  website: string
  hourlyRate: string
  callOutFee: string
  /** Standardværdier der kopieres til nye fakturaer — de kan altid rettes bagefter. */
  invoiceBankName: string
  invoiceRegistrationNumber: string
  invoiceAccountNumber: string
  invoicePaymentTerms: string
  invoicePaymentText: string
  invoiceFooterNote: string
}

export interface NewCaseInput {
  customerId: string
  newCustomer?: { name: string; phone: string; email: string; address: string }
  title: string
  address: string
  description: string
  category: CaseCategory
  priceModel: PriceModel
  hourlyRate: string
  fixedPrice: string
  plannedDate: string
  status: CaseStatus
  internalNote: string
  photos: { url: string; name: string; file?: File }[]
}

export interface EnquiryInput {
  name: string
  phone: string
  email: string
  address: string
  description: string
  files: File[]
  /** Id fra et tidligere mislykket forsøg — genbruges, så der ikke opstår dubletter. */
  draftId?: string
}

export const caseStatuses: CaseStatus[] = [
  'Ny henvendelse',
  'Kontaktet',
  'Tilbud sendt',
  'Accepteret',
  'Planlagt',
  'I gang',
  'Færdig',
  'Faktureret',
]

export const caseCategories: CaseCategory[] = ['Multiservice', 'Montage', 'Vedligehold', 'Andet']

export const priceModels: PriceModel[] = ['Timepris', 'Fast pris', 'Tilbud endnu ikke lavet']

export const photoStages: PhotoStage[] = ['Før', 'Under arbejdet', 'Efter']
